import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
// UPPDRAG-8 PR-E: dual-write reschedule system messages.
import { sendLeagueMatchMessage } from "@/lib/chatDualWrite";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Check, X, Clock } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { toast } from "sonner";
import { formatPrice } from "@/utils/formatPrice";
import RescheduleRequest from "./RescheduleRequest";
import { useTranslation } from "react-i18next";
import { buildAuditLogEntry } from "@/lib/rescheduleAuditHelpers";

interface RescheduleCardProps {
  request: {
    id: string;
    match_id: string;
    requested_by: string;
    proposed_date: string;
    proposed_time: string;
    status: string;
    admin_status?: string;
    fee_amount?: number;
    // Sprint D PR-AC Phase 4 dual-state cols.
    fee_amount_minor?: number | null;
    currency?: string;
  };
  matchId: string;
}

export default function RescheduleCard({ request, matchId }: RescheduleCardProps) {
  // PR-AV: useTranslation() must be called at component scope.
  const { t } = useTranslation();
  const { user } = useAuth();
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const [showCounter, setShowCounter] = useState(false);
  const isPending = request.status === "pending";
  const isOwn = request.requested_by === user?.id;
  const adminStatus = (request as Record<string, unknown>).admin_status ?? "pending";

  const respondMutation = useMutation({
    mutationFn: async (accept: boolean) => {
      if (!user) throw new Error("Inte inloggad");

      const newStatus = accept ? "accepted" : "rejected";

      // Update request status (opponent accepts/rejects)
      const { error: reqErr } = await supabase
        .from("reschedule_requests" as any)
        .update({
          status: newStatus,
          responded_by: user.id,
          responded_at: new Date().toISOString(),
        })
        .eq("id", request.id);
      if (reqErr) throw reqErr;

      // PR-7.2: audit-log entry. action follows the response.
      const auditEntry = buildAuditLogEntry({
        action: accept ? "opponent_accepted" : "opponent_rejected",
        requestId: request.id,
        actorId: user.id,
        actorRole: "player",
        metadata: {
          proposed_date: request.proposed_date,
          proposed_time: request.proposed_time,
        },
      });
      await supabase.from("reschedule_audit_log" as never).insert(auditEntry as never);

      // System message
      const displayDate = format(new Date(request.proposed_date), "d MMM", { locale: sv });
      const displayTime = request.proposed_time.slice(0, 5);

      if (accept) {
        // Do NOT move the match yet — wait for admin approval
        const msg = `✅ Båda lagen har godkänt ombokning till ${displayDate} kl ${displayTime} — väntar på admin-godkännande`;
        // UPPDRAG-8 PR-E: dual-write + structured metadata so future
        // renderer can replace the emoji-prefixed text with a card.
        await sendLeagueMatchMessage(supabase, {
          matchId,
          senderUserId: user.id,
          body: msg,
          isSystem: true,
          data: {
            type: "reschedule_both_accepted",
            reschedule_request_id: request.id,
            proposed_date: request.proposed_date,
            proposed_time: request.proposed_time,
          },
        });

        // Notify admins (find admin user_ids)
        try {
          if (organization) {
            const { data: adminRoles } = await supabase
              .from("user_roles")
              .select("user_id")
              .eq("role", "admin")
              .eq("organization_id", organization.id);
            if (adminRoles && adminRoles.length > 0) {
              await supabase.from("notifications").insert(
                adminRoles.map((r) => ({
                  user_id: r.user_id,
                  organization_id: organization.id,  // PR-B: org-scope per Phase 1 RLS (gated by `if (organization)` above)
                  title: "Ombokningsförfrågan väntar",
                  body: `Båda lagen har godkänt ombokning till ${displayDate} kl ${displayTime}. Godkänn eller neka.`,
                  type: "reschedule_admin_pending",
                  data: { match_id: matchId, reschedule_id: request.id },
                }))
              );
            }
          }
        } catch {
          // Non-critical
        }
      } else {
        const msg = `❌ Ombokningsförslag nekades`;
        // UPPDRAG-8 PR-E: dual-write + structured metadata.
        await sendLeagueMatchMessage(supabase, {
          matchId,
          senderUserId: user.id,
          body: msg,
          isSystem: true,
          data: {
            type: "reschedule_rejected",
            reschedule_request_id: request.id,
          },
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reschedule-requests", matchId] });
      queryClient.invalidateQueries({ queryKey: ["match-chat", matchId] });
      queryClient.invalidateQueries({ queryKey: ["match-detail", matchId] });
    },
    onError: (err: Error) => {
      toast.error(err.message || t("common:somethingWentWrong"));
    },
  });

  const displayDate = format(new Date(request.proposed_date), "d MMM yyyy", { locale: sv });
  const displayTime = request.proposed_time.slice(0, 5);

  const getStatusLabel = () => {
    if (request.status === "accepted" && adminStatus === "pending") return "Väntar på admin";
    if (request.status === "accepted" && adminStatus === "approved") return "Godkänd av admin";
    if (request.status === "accepted" && adminStatus === "denied") return "Nekad av admin";
    if (request.status === "accepted") return "Accepterad";
    if (request.status === "rejected") return "Nekad";
    return "Väntar på svar";
  };

  const getStatusColor = () => {
    if (request.status === "accepted" && adminStatus === "approved") return "text-green-600";
    if (request.status === "accepted" && adminStatus === "denied") return "text-destructive";
    if (request.status === "accepted" && adminStatus === "pending") return "text-amber-600";
    if (request.status === "rejected") return "text-destructive";
    return "text-muted-foreground";
  };

  if (showCounter) {
    return (
      <div className="max-w-[85%] mx-auto">
        {/* PR-7.2: pass parentRequestId so RescheduleRequest INSERTs with
            the FK + auto-rejects this request + records the
            counter_proposed audit entry chained via parent. */}
        <RescheduleRequest
          matchId={matchId}
          onClose={() => setShowCounter(false)}
          parentRequestId={request.id}
        />
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-3 bg-muted/30 space-y-2 max-w-[85%] mx-auto">
      <div className="flex items-center gap-2 text-sm font-medium">
        <CalendarDays className="h-4 w-4 text-primary" />
        Ombokningsförslag
      </div>
      <p className="text-sm">
        {displayDate} kl {displayTime}
      </p>
      {/* Sprint D PR-AC Phase 4-aware: prefer fee_amount_minor (öre → kr). */}
      {(() => {
        const feeKr = request.fee_amount_minor != null
          ? request.fee_amount_minor / 100
          : (request.fee_amount ?? 0);
        return feeKr > 0 ? (
          <Badge variant="destructive" className="text-xs">
            ⚠️ Avgift: {formatPrice(feeKr)}
          </Badge>
        ) : null;
      })()}
      <p className={`text-xs ${getStatusColor()}`}>{getStatusLabel()}</p>

      {isPending && !isOwn && (
        <div className="flex gap-2 pt-1">
          <Button
            size="sm"
            variant="default"
            className="flex-1"
            disabled={respondMutation.isPending}
            onClick={() => respondMutation.mutate(true)}
          >
            <Check className="h-3 w-3 mr-1" /> Acceptera
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            disabled={respondMutation.isPending}
            onClick={() => setShowCounter(true)}
          >
            <Clock className="h-3 w-3 mr-1" /> Annan tid
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            disabled={respondMutation.isPending}
            onClick={() => respondMutation.mutate(false)}
          >
            <X className="h-3 w-3 mr-1" /> Neka
          </Button>
        </div>
      )}
    </div>
  );
}
